import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import { DndContext, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Volume2, RotateCcw, Trophy, Star, Home, Shuffle, Target } from 'lucide-react';
import useAudio from '../../hooks/useAudio';
import useNumbersGame from '../../hooks/useNumbersGame';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

// Scoring constants - same as other games
const SCORING_CONSTANTS = {
  BASE_POINTS: 15,
  STREAK_MULTIPLIER: 2,
  SPEED_BONUS_MAX: 50,
  ACCURACY_BONUS_MAX: 100,
  PERFECT_ACCURACY_BONUS: 150,
  LEVEL_COMPLETE_BONUS: 100,
  MAX_SPEED_BONUS_TIME: 15, // seconds per sequence
  MIN_ACCURACY_FOR_STARS: 70
};

// Level configurations
const LEVEL_CONFIGS = {
  1: { 
    range: [0, 5], 
    numbersPerSequence: 4, 
    sequencesPerLevel: 10,
    title: "Baby Steps",
    description: "Sort numbers 0-5!",
    passingScore: 80
  },
  2: { 
    range: [0, 10], 
    numbersPerSequence: 5, 
    sequencesPerLevel: 10,
    title: "Getting Started",
    description: "Sort numbers 0-10!",
    passingScore: 80
  },
  3: { 
    range: [0, 20], 
    numbersPerSequence: 6, 
    sequencesPerLevel: 10,
    title: "Twenty Club",
    description: "Sort numbers 0-20!",
    passingScore: 80
  },
  4: { 
    range: [0, 50], 
    numbersPerSequence: 7, 
    sequencesPerLevel: 10,
    title: "Fifty Challenge",
    description: "Sort numbers 0-50!",
    passingScore: 85
  },
  5: { 
    range: [0, 100], 
    numbersPerSequence: 8, 
    sequencesPerLevel: 10,
    title: "Century Master",
    description: "Sort numbers 0-100!",
    passingScore: 85
  },
  6: { 
    range: [0, 150], 
    numbersPerSequence: 9, 
    sequencesPerLevel: 10,
    title: "Advanced Sorting",
    description: "Sort numbers 0-150!",
    passingScore: 85
  },
  7: { 
    range: [0, 200], 
    numbersPerSequence: 10, 
    sequencesPerLevel: 10,
    title: "Ultimate Sequencer",
    description: "Master all numbers 0-200!",
    passingScore: 90
  }
};

const ArabicNumberSequencingGame = () => {
  // Initialize hooks
  const { 
    userId,
    level,
    setLevel,
    maxLevel,
    numbersData,
    bestScores,
    isLoading,
    error,
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    resetAllProgress
  } = useNumbersGame('ArabicNumberSequencingGame');

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  const { saveSessionData } = useAnalytics();

  // Refs for audio management
  const audioRef = useRef(null);
  const audioTimeoutRef = useRef(null);
  
  // Game state
  const [gamePhase, setGamePhase] = useState('menu'); // 'menu', 'playing', 'complete'
  const [currentSequenceIndex, setCurrentSequenceIndex] = useState(0);
  const [currentNumbers, setCurrentNumbers] = useState([]);
  const [currentOrder, setCurrentOrder] = useState([]);
  const [correctOrder, setCorrectOrder] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  
  // Scoring state
  const [totalScore, setTotalScore] = useState(0);
  const [correctSequences, setCorrectSequences] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [sequenceStartTime, setSequenceStartTime] = useState(Date.now());
  const [levelStartTime, setLevelStartTime] = useState(Date.now());
  const [speedBonuses, setSpeedBonuses] = useState(0);
  const [streakBonuses, setStreakBonuses] = useState(0);
  const [stars, setStars] = useState(0);
  
  // UI state
  const [feedback, setFeedback] = useState('');
  const [celebrations, setCelebrations] = useState(false);
  const [sparkles, setSparkles] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  
  // Get current level config
  const levelConfig = useMemo(() => LEVEL_CONFIGS[level] || LEVEL_CONFIGS[1], [level]);

  // Utility functions
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  // Audio playback
  const playNumberAudio = useCallback(async (number) => {
    console.log('🔊 Playing audio for number:', number);
    
    // Clear any existing audio and timeouts
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
      audioRef.current = null;
    }
    
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }

    const numberData = numbersData.find(n => n.englishNumeral === number);
    if (!numberData?.audioURL) {
      console.log(`No audio available for number ${number}`);
      return;
    }

    setAudioPlaying(true);

    try {
      const audio = new Audio();
      audioRef.current = audio;
      
      // Set up a safety timeout
      audioTimeoutRef.current = setTimeout(() => {
        console.log('🔊 Audio timeout - stopping playback');
        cleanupAudio();
      }, 5000);
      
      // Create cleanup function that removes listeners
      const cleanupAudio = () => {
        setAudioPlaying(false);
        if (audioTimeoutRef.current) {
          clearTimeout(audioTimeoutRef.current);
          audioTimeoutRef.current = null;
        }
        
        // Remove event listeners before cleanup
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
        
        // Clear the audio
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
        } catch (e) {
          // Ignore cleanup errors
        }
        
        audioRef.current = null;
      };
      
      const handleEnded = () => {
        console.log('🔊 Audio ended normally');
        cleanupAudio();
      };
      
      const handleError = (error) => {
        console.warn('🔊 Audio error for number', number, ':', error);
        cleanupAudio();
      };
      
      // Add event listeners
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      
      audio.volume = 0.8;
      audio.src = numberData.audioURL;
      
      await audio.play();
      
    } catch (error) {
      console.error('🔊 Failed to play audio:', error);
      setAudioPlaying(false);
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
    }
  }, [numbersData]);

  const lastCelebrationRef = useRef(0);
  const triggerCelebration = useCallback((isStreak = false, earnedStars = 0) => {
    // Throttle celebrations
    const now = Date.now();
    if (now - lastCelebrationRef.current < 500) {
      return;
    }
    lastCelebrationRef.current = now;
    
    setCelebrations(true);
    setSparkles(true);
    
    let colors = ['#00ff88', '#00ddff', '#8844ff'];
    let particleCount = 30;
    let shapes = ['circle'];
    
    if (earnedStars > 0) {
      colors = ['#FFD700', '#FFA500'];
      shapes = ['star'];
      particleCount = 50;
      playStarSound();
    } else if (isStreak) {
      colors = ['#FFD700', '#FFA500'];
      particleCount = 40;
      playStreakSound();
    } else {
      playCorrectSound();
    }
    
    confetti({
      particleCount,
      spread: 50,
      origin: { y: 0.7 },
      colors,
      shapes,
      gravity: 1.2,
      drift: 0
    });

    setTimeout(() => {
      setCelebrations(false);
      setSparkles(false);
    }, 1500);
  }, [playStarSound, playStreakSound, playCorrectSound]);

  const calculateSpeedBonus = useCallback((responseTime) => {
    const maxTime = SCORING_CONSTANTS.MAX_SPEED_BONUS_TIME * 1000;
    if (responseTime <= maxTime) {
      const bonus = Math.round(SCORING_CONSTANTS.SPEED_BONUS_MAX * (1 - responseTime / maxTime));
      return Math.max(0, bonus);
    }
    return 0;
  }, []);

  const calculateStars = useCallback((accuracy, totalScore, totalSequences) => {
    const maxPossibleScore = totalSequences * (SCORING_CONSTANTS.BASE_POINTS + SCORING_CONSTANTS.SPEED_BONUS_MAX);
    const scorePercentage = (totalScore / maxPossibleScore) * 100;
    
    if (accuracy >= 95 && scorePercentage >= 85) return 3;
    if (accuracy >= 85 && scorePercentage >= 70) return 2;
    if (accuracy >= 70 && scorePercentage >= 50) return 1;
    return 0;
  }, []);

  const generateSequence = useCallback(() => {
    if (!numbersData || numbersData.length === 0) {
      console.log('Numbers data not loaded yet');
      return false;
    }

    const [min, max] = levelConfig.range;
    let availableNumbers = numbersData
      .filter(n => n.englishNumeral >= min && n.englishNumeral <= max)
      .map(n => n.englishNumeral);
    
    // Randomly select numbers for this sequence
    const shuffledAvailable = shuffleArray(availableNumbers);
    const selectedNumbers = shuffledAvailable.slice(0, levelConfig.numbersPerSequence);
    
    // Sort for correct order
    const sorted = [...selectedNumbers].sort((a, b) => a - b);
    
    // Shuffle for current order
    const shuffled = shuffleArray([...selectedNumbers]);
    
    setCurrentNumbers(selectedNumbers);
    setCorrectOrder(sorted);
    setCurrentOrder(shuffled);
    setSequenceStartTime(Date.now());
    setFeedback('');
    
    return true;
  }, [levelConfig, numbersData, shuffleArray]);

  const handleLevelComplete = useCallback((finalCorrectSequences = null) => {
    const actualCorrectSequences = finalCorrectSequences !== null ? finalCorrectSequences : correctSequences;
    const totalSequences = levelConfig.sequencesPerLevel;
    const accuracy = Math.round((actualCorrectSequences / totalSequences) * 100);
    const accuracyBonus = accuracy === 100 ? 
      SCORING_CONSTANTS.PERFECT_ACCURACY_BONUS : 
      Math.round((accuracy / 100) * SCORING_CONSTANTS.ACCURACY_BONUS_MAX);
    
    const finalScore = totalScore + accuracyBonus + SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS;
    const earnedStars = calculateStars(accuracy, finalScore, totalSequences);
    
    const scoreData = {
      totalScore: finalScore,
      accuracy,
      stars: earnedStars,
      streak: bestStreak,
      speedBonuses,
      streakBonuses,
      accuracyBonus,
      levelCompleteBonus: SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS,
      completedAt: new Date().toISOString()
    };
    
    setStars(earnedStars);
    saveLevelData(level, scoreData);
    const sessionData = {
      gameType: 'arabicNumberSequencing',
      level,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: finalScore,
      accuracy,
      attempts: levelConfig.sequencesPerLevel,
      correctAnswers: actualCorrectSequences,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      levelTitle: levelConfig.title,
      bestStreak,
      speedBonuses,
      streakBonuses
    };
    saveSessionData(sessionData);
    
    const nextLevel = level + 1;
    const isLastLevel = !LEVEL_CONFIGS[nextLevel];
    const passedLevel = accuracy >= levelConfig.passingScore;
    
    setLevelCompleteData({
      ...scoreData,
      currentLevel: level,
      nextLevel,
      isLastLevel,
      passedLevel,
      levelTitle: levelConfig.title,
      passingScore: levelConfig.passingScore
    });
    
    if (earnedStars > 0) {
      triggerCelebration(false, earnedStars);
    }
    
    playPerfectSound();
    setGamePhase('complete');
    setShowLevelComplete(true);
    
    if (passedLevel && !isLastLevel) {
      unlockNextLevel();
    }
  }, [
    correctSequences,
    levelConfig.sequencesPerLevel,
    levelConfig.passingScore,
    levelConfig.title,
    totalScore,
    bestStreak,
    speedBonuses,
    streakBonuses,
    level,
    calculateStars,
    saveLevelData,
    triggerCelebration,
    playPerfectSound,
    unlockNextLevel
  ]);

  const nextSequence = useCallback(() => {
    const newIndex = currentSequenceIndex + 1;
    
    if (newIndex >= levelConfig.sequencesPerLevel) {
      handleLevelComplete(correctSequences);
    } else {
      setCurrentSequenceIndex(newIndex);
      generateSequence();
      playWhooshSound();
    }
  }, [currentSequenceIndex, levelConfig.sequencesPerLevel, correctSequences, generateSequence, playWhooshSound, handleLevelComplete]);

  const skipSequence = useCallback(() => {
    // Reset streak when skipping and move to next sequence
    setCurrentStreak(0);
    setFeedback('');
    
    playWhooshSound();
    
    const newIndex = currentSequenceIndex + 1;
    if (newIndex >= levelConfig.sequencesPerLevel) {
      handleLevelComplete(correctSequences);
    } else {
      setCurrentSequenceIndex(newIndex);
      generateSequence();
    }
  }, [currentSequenceIndex, levelConfig.sequencesPerLevel, correctSequences, handleLevelComplete, generateSequence, playWhooshSound]);

  const checkSequence = useCallback(() => {
    const isCorrect = currentOrder.every((num, index) => num === correctOrder[index]);
    
    if (isCorrect) {
      const responseTime = Date.now() - sequenceStartTime;
      const speedBonus = calculateSpeedBonus(responseTime);
      const newStreak = currentStreak + 1;
      
      let points = SCORING_CONSTANTS.BASE_POINTS;
      if (newStreak >= 3) {
        points *= SCORING_CONSTANTS.STREAK_MULTIPLIER;
        setStreakBonuses(prev => prev + (SCORING_CONSTANTS.BASE_POINTS * (SCORING_CONSTANTS.STREAK_MULTIPLIER - 1)));
      }
      
      setCorrectSequences(prev => prev + 1);
      setCurrentStreak(newStreak);
      setBestStreak(prev => Math.max(prev, newStreak));
      setTotalScore(prev => prev + points + speedBonus);
      setSpeedBonuses(prev => prev + speedBonus);
      setFeedback('correct');
      
      const isStreak = newStreak >= 3;
      triggerCelebration(isStreak);
      
      // Check if level complete
      const newCorrectCount = correctSequences + 1;
      if (newCorrectCount >= levelConfig.sequencesPerLevel) {
        setTimeout(() => {
          handleLevelComplete(newCorrectCount);
        }, 2000);
      } else {
        setTimeout(() => {
          nextSequence();
        }, 3000);
      }
    } else {
      setSessionMistakes(prev => [...prev, {
        timestamp: Date.now(),
        type: 'incorrect_sequence',
        attempted: currentOrder.join(', '),
        correct: correctOrder.join(', '),
        sequenceIndex: currentSequenceIndex
      }]);
      // Don't reset streak or move to next question - let them try again
      setFeedback('incorrect');
      playIncorrectSound();
      
      // Clear feedback after a delay but stay on the same question
      setTimeout(() => {
        setFeedback('');
      }, 2000);
    }
  }, [
    currentOrder,
    correctOrder,
    sequenceStartTime,
    currentStreak,
    correctSequences,
    levelConfig.sequencesPerLevel,
    calculateSpeedBonus,
    triggerCelebration,
    playIncorrectSound,
    handleLevelComplete,
    nextSequence
  ]);

  const startLevel = useCallback(() => {
    setSessionStartTime(Date.now()); 
    setSessionMistakes([]);
    setGamePhase('playing');
    setCurrentSequenceIndex(0);
    setTotalScore(0);
    setCorrectSequences(0);
    setCurrentStreak(0);
    setBestStreak(0);
    setSpeedBonuses(0);
    setStreakBonuses(0);
    setStars(0);
    setLevelStartTime(Date.now());
    
    setTimeout(() => {
      generateSequence();
    }, 100);
  }, [generateSequence]);

  const goToMenu = useCallback(() => {
    setGamePhase('menu');
  }, []);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    updateLevel(1);
    setGamePhase('menu');
    setShowResetConfirmation(false);
  }, [resetAllProgress, updateLevel]);

  const handleProceedToNextLevel = useCallback(() => {
    if (levelCompleteData?.passedLevel && !levelCompleteData?.isLastLevel) {
      updateLevel(levelCompleteData.nextLevel);
    }
    setShowLevelComplete(false);
    setLevelCompleteData(null);
    setGamePhase('menu');
  }, [levelCompleteData, updateLevel]);

  const shuffleCurrentNumbers = useCallback(() => {
    // Only shuffle the order of existing numbers, don't generate new ones
    const shuffled = shuffleArray([...currentNumbers]);
    setCurrentOrder(shuffled);
    setSequenceStartTime(Date.now()); // Reset timer for fair speed bonus
    setFeedback(''); // Clear any existing feedback
    playWhooshSound();
  }, [currentNumbers, shuffleArray, playWhooshSound]);

  const selectLevel = useCallback((newLevel) => {
    setLevel(newLevel);
  }, [setLevel]);

  // Drag and Drop handlers
  const handleDragStart = useCallback((event) => {
    console.log('Drag start:', event.active.id);
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    console.log('Drag end:', { activeId: active?.id, overId: over?.id });
    
    // Safety checks - be explicit about null/undefined vs falsy values like 0
    if (!active || !over || active.id === null || active.id === undefined || !over.id) {
      console.log('Drag end: Missing required data');
      setActiveId(null);
      return;
    }
    
    // Extract the slot number from the over ID (format: "slot-0", "slot-1", etc.)
    const overSlotMatch = over.id.toString().match(/^slot-(\d+)$/);
    if (!overSlotMatch) {
      console.log('Drag end: Invalid slot format');
      setActiveId(null);
      return;
    }
    
    const targetPosition = Number(overSlotMatch[1]); // Use Number() instead of parseInt()
    const draggedNumber = active.id;
    
    console.log('Moving number', draggedNumber, 'to position', targetPosition);
    
    setCurrentOrder((items) => {
      // Additional safety checks
      if (!items || items.length === 0) {
        console.log('Drag end: No items array');
        return items;
      }
      
      const currentPosition = items.findIndex(item => item === draggedNumber);
      
      console.log('Current position:', currentPosition, 'Target position:', targetPosition);
      
      // Ensure valid indices
      if (currentPosition === -1 || targetPosition >= items.length || targetPosition < 0) {
        console.log('Drag end: Invalid positions');
        return items;
      }
      
      // Don't do anything if dropping in the same position
      if (currentPosition === targetPosition) {
        console.log('Drag end: Same position, no change needed');
        return items;
      }
      
      const newOrder = arrayMove(items, currentPosition, targetPosition);
      console.log('New order:', newOrder);
      return newOrder;
    });
    
    setActiveId(null);
  }, []);

  // Initialize first sequence when data loads
  useEffect(() => {
    if (numbersData && numbersData.length > 0 && gamePhase === 'playing') {
      generateSequence();
    }
  }, [numbersData, gamePhase, generateSequence]);

  // Start background music
  useEffect(() => {
    const musicTimer = setTimeout(() => {
      startBackgroundMusic();
    }, 1000);
    return () => clearTimeout(musicTimer);
  }, [startBackgroundMusic]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.removeEventListener('ended', audioRef.current._endHandler);
          audioRef.current.removeEventListener('error', audioRef.current._errorHandler);
          audioRef.current.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+Dyvmya';
          audioRef.current.load();
        } catch (e) {
          // Ignore
        }
        audioRef.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      
      // Clear confetti
      confetti.reset();
    };
  }, []);

  // Components
  const StarDisplay = React.memo(({ count, size = "text-2xl" }) => {
    return (
      <div className={`flex justify-center gap-1 ${size}`}>
        {[1, 2, 3].map(star => (
          <span 
            key={star} 
            className={`drop-shadow-lg transition-all duration-300 ${star <= count ? 'text-yellow-400 animate-pulse' : 'text-gray-300'}`}
          >
            ⭐
          </span>
        ))}
      </div>
    );
  });

  StarDisplay.propTypes = {
    count: PropTypes.number.isRequired,
    size: PropTypes.string
  };

  const DraggableNumber = React.memo(({ number }) => {
    const numberData = numbersData.find(n => n.englishNumeral === number);
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id: number,
    });

    const style = transform ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      zIndex: isDragging ? 1000 : 'auto',
    } : {};

    if (!numberData) return null;

    const handleAudioClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      console.log('Audio button clicked for number:', number);
      playNumberAudio(number);
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`
          relative w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-blue-400 to-purple-500 rounded-3xl shadow-xl 
          flex flex-col items-center justify-center text-white font-bold
          border-4 border-white hover:scale-105 transition-all duration-200
          ${isDragging ? 'opacity-50 scale-105' : ''}
        `}
      >
        {/* Drag handle area - main content */}
        <div 
          {...listeners} 
          {...attributes}
          className="w-full h-full cursor-grab active:cursor-grabbing flex flex-col items-center justify-center rounded-3xl"
          style={{ touchAction: 'none' }} // Enables dragging on mobile
        >
          <div className="text-4xl md:text-5xl mb-2">{numberData.arabicNumeral}</div>
          <div className="text-sm md:text-base text-center px-2">{numberData.arabicWord}</div>
        </div>
        
        {/* Large, separate audio button */}
        <div className="absolute -top-4 -right-4">
          <button
            onClick={handleAudioClick}
            disabled={audioPlaying}
            className="w-12 h-12 bg-yellow-400 text-yellow-800 rounded-full shadow-xl hover:bg-yellow-300 hover:scale-110 transition-all duration-200 border-3 border-white flex items-center justify-center"
            style={{ 
              pointerEvents: 'auto',
              touchAction: 'manipulation'
            }}
          >
            <Volume2 size={24} />
          </button>
        </div>
      </div>
    );
  });

  const DroppableSlot = React.memo(({ position }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: `slot-${position}`,
    });

    return (
      <div
        ref={setNodeRef}
        className={`
          w-32 h-32 md:w-40 md:h-40 border-4 border-dashed rounded-3xl flex items-center justify-center
          transition-all duration-200 ${
            isOver 
              ? 'border-green-400 bg-green-100 scale-105' 
              : 'border-gray-400 bg-gray-50'
          }
        `}
      >
        <DraggableNumber number={currentOrder[position]} />
      </div>
    );
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">🔢</div>
            <div className="text-purple-600 text-xl md:text-3xl font-bold animate-pulse">Loading Sequence Master...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Getting ready for sorting fun!</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="text-4xl md:text-6xl mb-4">😢</div>
          <h2 className="text-xl md:text-3xl font-bold text-red-600 mb-4">Oops! Something went wrong</h2>
          <p className="text-gray-700 mb-6 text-sm md:text-lg">{error?.message || 'Something went wrong'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 p-4">
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            🔢 Sequence Master 🔢
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Sort Arabic numbers from smallest to largest!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-4xl mx-auto">
            {/* Level Info */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-4 md:mb-8 border-4 border-yellow-300">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-purple-600">
                🎮 Level {level}: {levelConfig.title}
              </h2>
              <p className="text-center text-gray-700 mb-4 md:mb-6 text-lg md:text-xl font-semibold">
                {levelConfig.description}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
                <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎯</div>
                  <p className="font-bold text-sm md:text-lg">Range</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.range[0]}-{levelConfig.range[1]}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🔢</div>
                  <p className="font-bold text-sm md:text-lg">Per Sequence</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.numbersPerSequence}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">📝</div>
                  <p className="font-bold text-sm md:text-lg">Sequences</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.sequencesPerLevel}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🏆</div>
                  <p className="font-bold text-sm md:text-lg">Pass</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.passingScore}%</p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={startLevel}
                  className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
                >
                  🚀 START SORTING! 🚀
                </button>
              </div>
            </div>

            {/* Level Grid */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 border-4 border-pink-300">
              <h3 className="text-xl md:text-3xl font-bold text-center mb-4 md:mb-6 text-purple-600">
                🎮 Choose Your Level!
              </h3>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-4">
                {Object.entries(LEVEL_CONFIGS).map(([levelNum, config]) => {
                  const num = parseInt(levelNum);
                  const isUnlocked = num <= maxLevel;
                  const isCurrent = num === level;
                  const levelScore = bestScores[`level${num}`];
                  
                  return (
                    <button
                      key={num}
                      onClick={() => isUnlocked ? selectLevel(num) : null}
                      disabled={!isUnlocked}
                      className={`
                        relative p-2 md:p-4 rounded-2xl text-sm md:text-lg font-bold transition-all duration-300 min-h-[80px] md:min-h-[120px] flex flex-col items-center justify-center border-2 md:border-4
                        ${isCurrent 
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl transform scale-105 md:scale-110 border-yellow-300' 
                          : isUnlocked 
                            ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-800 hover:from-green-200 hover:to-green-300 cursor-pointer hover:scale-105 border-green-300' 
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300'
                        }
                      `}
                      title={isUnlocked ? config.title : 'Locked'}
                    >
                      <div className="text-lg md:text-2xl font-black">{num}</div>
                      <div className="text-xs mt-1 font-bold hidden md:block">{config.range[0]}-{config.range[1]}</div>
                      <div className="text-xs font-bold md:hidden">{config.range[0]}-{config.range[1]}</div>
                      {levelScore && (
                        <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                          <StarDisplay count={levelScore.stars || 0} size="text-xs md:text-sm" />
                        </div>
                      )}
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xl md:text-3xl">🔒</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 md:mt-8 flex gap-2 md:gap-4 justify-center flex-wrap">
                <Link to="/">
                  <button 
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    🔙 Main Menu
                  </button>
                </Link>                
                <button 
                  onClick={handleResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🗑️ Reset Progress
                </button>
                <button 
                  onClick={() => setShowInstructions(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  📖 How to Play
                </button>
              </div>
            </div>
          </div>
        )}

  {gamePhase === 'playing' && (
          <div className="max-w-4xl mx-auto">
            {/* Progress and Stats */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl md:text-2xl font-bold text-purple-600">
                  Sequence {currentSequenceIndex + 1} of {levelConfig.sequencesPerLevel}
                </h3>
                <div className="text-right">
                  <p className="text-lg font-bold text-indigo-600">{totalScore} points</p>
                  <p className="text-sm text-gray-600">{correctSequences} correct</p>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div 
                  className="bg-gradient-to-r from-green-400 to-green-500 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${(currentSequenceIndex / levelConfig.sequencesPerLevel) * 100}%` }}
                ></div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🎯</div>
                  <p className="font-bold text-lg md:text-xl">{totalScore}</p>
                  <p className="text-xs font-bold">Score</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">✅</div>
                  <p className="font-bold text-lg md:text-xl">{correctSequences}/{levelConfig.sequencesPerLevel}</p>
                  <p className="text-xs font-bold">Complete</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🔥</div>
                  <p className="font-bold text-lg md:text-xl">{currentStreak}</p>
                  <p className="text-xs font-bold">Streak</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🏆</div>
                  <p className="font-bold text-lg md:text-xl">{bestStreak}</p>
                  <p className="text-xs font-bold">Best</p>
                </div>
              </div>
              
              {currentStreak >= 3 && (
                <div className="mt-4 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-3">
                  <p className="text-white font-bold text-lg animate-pulse">
                    🔥 {currentStreak} in a row! You're on fire! 🔥
                  </p>
                </div>
              )}
            </div>

            {/* Game Board */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-6 border-4 border-yellow-300">
              <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                  🎯 Sort from Smallest to Largest
                </h2>
                <p className="text-gray-600 text-lg">Drag and drop the numbers in the correct order!</p>
                <p className="text-sm text-gray-500 mt-2">💡 Tap the volume button on any number to hear its pronunciation</p>
              </div>

              <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <div className="flex flex-wrap justify-center gap-6 mb-8 min-h-[200px] items-center">
                  {currentOrder.map((number, index) => (
                    <DroppableSlot key={`slot-${index}`} position={index} />
                  ))}
                </div>
                
                <DragOverlay>
                  {activeId ? <DraggableNumber number={activeId} /> : null}
                </DragOverlay>
              </DndContext>

              {/* Feedback */}
              {feedback && (
                <div className={`text-center mb-6 p-4 rounded-2xl border-4 ${
                  feedback === 'correct' 
                    ? 'text-green-600 bg-green-100 border-green-300' 
                    : 'text-red-600 bg-red-100 border-red-300'
                }`}>
                  <div className={`text-3xl mb-2 ${
                    feedback === 'correct' ? 'animate-bounce' : 'animate-pulse'
                  }`}>
                    {feedback === 'correct' ? '🎉' : '❌'}
                  </div>
                  <p className="text-xl font-bold">
                    {feedback === 'correct' ? 
                      'Perfect! Great sorting!' : 
                      'Not quite right. Try again!'
                    }
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 justify-center">
                <button 
                  onClick={checkSequence}
                  disabled={feedback !== ''}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ✅ Check Order
                </button>
                <button 
                  onClick={shuffleCurrentNumbers}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2">
                  <Shuffle size={20} />
                  Shuffle
                </button>
                <button 
                  onClick={skipSequence}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2">
                  <Target size={20} />
                  Give Up
                </button>
              </div>
            </div>

            {/* Controls */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-xl">
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={goToMenu}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Home size={20} />
                  Menu
                </button>
                <button 
                  onClick={startLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <RotateCcw size={20} />
                  Restart Level
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Celebration Effects */}
        {celebrations && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {sparkles && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-purple-600">📖 How to Play Sequence Master!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-xl">🎯 Your Mission</h3>
                  <p className="text-blue-700 text-sm md:text-lg">Sort Arabic numbers from smallest to largest by dragging and dropping them in the correct order!</p>
                </div>
                
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-xl">🎮 How to Play</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>You'll see Arabic numbers mixed up randomly</li>
                    <li>Drag and drop them to put them in order from smallest to largest</li>
                    <li>Click "Check Order" when you think they're correct</li>
                    <li>If wrong, you can try again unlimited times!</li>
                    <li>Complete 10 sequences to finish the level</li>
                    <li>Get 8 out of 10 correct to unlock the next level!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                  <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-xl">🔊 Audio Help</h3>
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Tap the large yellow speaker button on any number to hear its pronunciation</li>
                    <li>Use audio to help identify unfamiliar numbers</li>
                    <li>This helps you learn the Arabic number names!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-xl">🎮 Game Controls</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li><strong>Check Order:</strong> Verify if your sequence is correct</li>
                    <li><strong>Shuffle:</strong> Mix up the current numbers again</li>
                    <li><strong>Give Up:</strong> Skip to the next sequence if you're stuck</li>
                    <li>Wrong answers let you try again - no pressure!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-red-100 to-red-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-red-300">
                  <h3 className="font-bold text-red-800 mb-2 md:mb-3 text-lg md:text-xl">💡 Pro Tips</h3>
                  <ul className="list-disc list-inside text-red-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Look for the Arabic number shapes you recognize</li>
                    <li>Use the audio to help identify unfamiliar numbers</li>
                    <li>Start with the smallest and largest numbers first</li>
                    <li>Take your time - accuracy is more important than speed</li>
                    <li>Use "Shuffle" if you get stuck on a sequence</li>
                    <li>Build streaks of correct answers for bonus points!</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 md:mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Let's Start Sorting! 🔢
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Level Complete Modal */}
      {showLevelComplete && levelCompleteData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-4 md:p-8 border-4 border-yellow-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">
                {levelCompleteData.passedLevel ? '🎉' : '😊'}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-purple-600 mb-2 md:mb-3">
                Level {levelCompleteData.currentLevel} Complete!
              </h2>
              <h3 className="text-lg md:text-xl text-gray-700 mb-4 md:mb-6 font-semibold">
                {levelCompleteData.levelTitle}
              </h3>
              
              <div className="mb-6 md:mb-8">
                <StarDisplay count={levelCompleteData.stars} size="text-3xl md:text-4xl" />
                <p className="text-xl md:text-2xl font-bold text-indigo-600 mt-3 md:mt-4">
                  Final Score: {levelCompleteData.totalScore}
                </p>
                <p className="text-base md:text-lg text-gray-600 mt-2">
                  Accuracy: {levelCompleteData.accuracy}% • Best Streak: {levelCompleteData.streak}
                </p>
              </div>

              <div className="mb-6 md:mb-8">
                {levelCompleteData.passedLevel ? (
                  <div className="bg-gradient-to-r from-green-100 to-green-200 border-2 md:border-4 border-green-400 rounded-2xl p-4 md:p-6">
                    <p className="text-green-800 font-bold text-lg md:text-xl">
                      ✅ Fantastic! You passed! 🎉
                    </p>
                    <p className="text-green-700 text-base md:text-lg">
                      {levelCompleteData.accuracy}% ≥ {levelCompleteData.passingScore}%
                    </p>
                    {!levelCompleteData.isLastLevel && (
                      <p className="text-green-700 text-base md:text-lg mt-2">Let's now conquer level {levelCompleteData.nextLevel}</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-orange-100 to-orange-200 border-2 md:border-4 border-orange-400 rounded-2xl p-4 md:p-6">
                    <p className="text-orange-800 font-bold text-lg md:text-xl">
                      😊 Good try! Practice makes perfect!
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">
                      You need {levelCompleteData.passingScore}% accuracy to pass
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">Try again to unlock the next level!</p>
                  </div>
                )}
              </div>

              <div className="space-y-3 md:space-y-4">
                <button 
                  onClick={() => { 
                    setShowLevelComplete(false); 
                    setGamePhase('menu'); 
                  }}
                  className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔄 Play Again
                </button>
                
                {levelCompleteData.passedLevel && !levelCompleteData.isLastLevel && (
                  <button 
                    onClick={handleProceedToNextLevel}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🚀 Next Level! 🚀
                  </button>
                )}
                
                {levelCompleteData.isLastLevel && (
                  <button 
                    onClick={() => { 
                      setShowLevelComplete(false); 
                      setGamePhase('menu'); 
                    }}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🎊 You're a Sequence Master! 🎊
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">⚠️</div>
              <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-3 md:mb-4">Reset Everything?</h2>
              <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
                This will delete all your amazing scores, stars, and progress. You'll start from Level 1 again.
                This cannot be undone!
              </p>
              <div className="flex gap-3 md:gap-4 justify-center">
                <button 
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Yes, Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArabicNumberSequencingGame;
